// props.json as written by the pipeline, one file per converted IFC layer, plus the readers.

export interface PropsFile {
  id: string;
  source: string;
  generatedAt?: string;
  units?: { length: 'm' };
  storeys: Storey[];
  elements: PropsElement[];
}

export interface Storey {
  expressID: number;
  name: string;
  elevation: number | null;
}

export interface PropsElement {
  expressID: number;
  globalId: string;
  type: string;
  name: string;
  storeyId: number | null;
  storey: string | null;
  psets?: Record<string, Record<string, unknown>>;
  quantities?: Record<string, Record<string, number>>;
  material?: Material;
  derived?: { volume: number };
}

export interface Material {
  kind: 'single' | 'list' | 'layers' | 'profiles' | 'constituents';
  names: string[];
  setName?: string;
  layers?: MaterialLayer[];
  viaType?: boolean;
}

export interface MaterialLayer {
  name: string;
  thickness: number;
}

export type VolumeSource = 'ifc' | 'revit' | 'geometry';

// Revit exports unset properties as their own name ("FireRating" = "FireRating"); those count as null.
export function prop(el: PropsElement | undefined, pset: string, name: string): string | null {
  const v = el?.psets?.[pset]?.[name];
  if (v == null) return null;
  const s = String(v);
  return s === '' || fold(s) === fold(name) ? null : s;
}

const fold = (s: string) => s.replace(/[\s_-]/g, '').toLowerCase();

export function num(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function isType(el: PropsElement, prefix: string): boolean {
  return el.type.toUpperCase().startsWith(prefix.toUpperCase());
}

export function quantity(el: PropsElement, name: string): number | null {
  for (const set of Object.values(el.quantities ?? {})) {
    const v = set[name];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
}

export function volumeOf(el: PropsElement): { value: number; source: VolumeSource } | null {
  const net = quantity(el, 'NetVolume') ?? quantity(el, 'GrossVolume');
  if (net != null) return { value: net, source: 'ifc' };
  const revit = num(prop(el, 'PSet_Revit_Dimensions', 'Volume'));
  if (revit != null) return { value: revit, source: 'revit' };
  if (el.derived?.volume != null) return { value: el.derived.volume, source: 'geometry' };
  return null;
}

export function areaOf(el: PropsElement): { value: number; source: VolumeSource } | null {
  const q =
    quantity(el, 'NetFloorArea') ??
    quantity(el, 'GrossFloorArea') ??
    quantity(el, 'NetArea') ??
    quantity(el, 'GrossArea') ??
    quantity(el, 'NetSideArea');
  if (q != null) return { value: q, source: 'ifc' };
  const revit = num(prop(el, 'PSet_Revit_Dimensions', 'Area'));
  if (revit != null) return { value: revit, source: 'revit' };
  return null;
}

export function materialNames(el: PropsElement): string[] {
  return (el.material?.names ?? []).map((n) => n.toLowerCase());
}

// Share of the element made of matching material: thickness ratio for layer sets, all or nothing otherwise.
export function materialShare(m: Material | undefined, match: (name: string) => boolean): number {
  if (!m) return 0;
  if (m.kind === 'layers' && m.layers?.length) {
    const total = m.layers.reduce((s, l) => s + l.thickness, 0);
    if (total <= 0) return m.layers.some((l) => match(l.name)) ? 1 : 0;
    return m.layers.filter((l) => match(l.name)).reduce((s, l) => s + l.thickness, 0) / total;
  }
  return m.names.some(match) ? 1 : 0;
}

export function isMixed(m: Material | undefined): boolean {
  return !!m && m.kind !== 'layers' && m.names.length > 1;
}
